import { BIOMES, WORLD_NODES, isSlotNodeType, isSlotUnlocked } from '@goodtime/shared';

/**
 * The map button at the top left of a slot (and the wheel lobby): the way back, and a reminder of how much is out there
 * (producer 2026-11-03: "They've no idea how many worlds or slots are waiting for them"). Pure, so the counts and the
 * plate's fit are tested without a browser; {@link BackToMapButton} draws it.
 */

export interface MapButtonInfo {
  /** Where the button leads: the slot's world (its map), else all the worlds. */
  title: string;
  /** The games in the slot's world the player has not opened yet (the badge; 0 hides it). */
  waiting: number;
  worlds: number;
  games: number;
}

const SLOT_NODES = WORLD_NODES.filter((n) => isSlotNodeType(n.type));
const WORLD_COUNT = new Set(SLOT_NODES.map((n) => n.biome)).size;

/** What the button says. `nodeId` is the open slot's node, or null away from a slot. */
export function mapButtonInfo(nodeId: number | null, unlockedSlotIds: ReadonlyArray<string>): MapButtonInfo {
  const node = nodeId === null ? null : (WORLD_NODES.find((n) => n.id === nodeId) ?? null);
  const base = { worlds: WORLD_COUNT, games: SLOT_NODES.length };
  if (!node) return { title: 'All worlds', waiting: 0, ...base };
  const waiting = SLOT_NODES.filter((n) => n.biome === node.biome && !isSlotUnlocked(n.id, unlockedSlotIds)).length;
  return { title: BIOMES[node.biome]?.name ?? 'All worlds', waiting, ...base };
}

/** The plate's second line. */
export function countsLine(info: MapButtonInfo): string {
  return `${info.worlds} worlds · ${info.games} games`;
}

/** The counts as two short lines, for a phone's narrow gap beside the wallet pill. */
export function countsStacked(info: MapButtonInfo): [string, string] {
  return [`${info.worlds} worlds`, `${info.games} games`];
}

/** What the button's label reads to a screen reader. */
export function mapButtonLabel(info: MapButtonInfo, disabled: boolean): string {
  const waiting = info.waiting > 0 ? ` ${info.waiting} more ${info.waiting === 1 ? 'game' : 'games'} to open here.` : '';
  const label = `Back to ${info.title}.${waiting} ${info.worlds} worlds, ${info.games} games in all.`;
  return disabled ? `${label} Finish the Lamp Run first.` : label;
}

/** The ring, CSS px, and how far the plate tucks under it. */
export const MAP_RING = 52;
export const PLATE_TUCK = 14;
/** The plate's padding and border either side of its words. */
export const PLATE_CHROME = 20 + 13 + 2;
/** Room kept between the plate and the wallet pill. */
export const PILL_GAP = 8;

/** How much of the plate is shown: both lines, both smaller, the counts alone on one line, the counts on two short lines
 *  (a phone), or none (the ring and its badge alone). */
export type PlateFit = 'full' | 'small' | 'counts' | 'stacked' | 'none';

/** The words' widths at each size (measured by the button from the loaded fonts). */
export interface PlateWidths {
  full: number;
  small: number;
  counts: number;
  stacked: number;
}

/** The largest plate whose width fits `room` (from the plate's left edge to the wallet pill, less {@link PILL_GAP}). */
export function pickFit(room: number, words: PlateWidths): PlateFit {
  for (const fit of ['full', 'small', 'counts', 'stacked'] as const) if (words[fit] + PLATE_CHROME <= room) return fit;
  return 'none';
}
